import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { HTTPError } from "ky";
import type { AppDispatch, RootState } from "@/store/store";
import userService from "@/endpoints/userService";
import { viewDetailLoaded } from "@/store/slices/viewsV2/viewsV2Slice";
import { isViewsV2Enabled } from "@/utils/viewDefinition/flag";
import { useHasActiveSession } from "@/auth/useHasActiveSession";

export type ViewLandingStatus = "idle" | "loading" | "loaded" | "gone";

/**
 * Only these two statuses mean "the view is actually gone" (matches the
 * `VIEW_NOT_FOUND` / `SCHEMA_VERSION_UNSUPPORTED` discrimination in
 * `saveViewV2.ts`'s `mapError`). Any other failure -- 500, a network drop, an
 * unparsable body -- is transient/unknown and must NOT show the "no longer
 * exists" copy; the fetch simply didn't succeed this time.
 */
const GONE_STATUS_CODES = new Set([404, 422]);

export interface UseViewLandingResult {
  status: ViewLandingStatus;
}

/**
 * Landing hook for opening a saved view via its server-computed `?viewId=`.
 *
 * URL-primary hydration: the screen always renders from the URL's own
 * params regardless of this hook's outcome. This hook only fetches the
 * server view detail (for `ViewProvenanceNotice`) and records last-access
 * -- it never touches `userSelection` and never blocks rendering.
 *
 * Fires exactly once per viewId (ref-guarded, mirrors `useInitialURLBootstrap`),
 * and only when the Views V2 flag is on and `?viewId=` is present.
 */
export const useViewLanding = (): UseViewLandingResult => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [status, setStatus] = useState<ViewLandingStatus>("idle");
  const lastViewIdRef = useRef<string | null>(null);
  // This hook mounts outside <Auth>, so on a cold landing it can run before the
  // token exists. An unauthenticated GET 401s, and the ky hook clears the token
  // and hard-redirects to login -- a saved-view link would bounce the user out.
  // Subscribing to userDetail re-runs the effect once auth has settled.
  const userId = useSelector((state: RootState) => state.userDetail?.id);
  const hasSession = useHasActiveSession();

  useEffect(() => {
    if (!isViewsV2Enabled()) return;

    const viewId = new URLSearchParams(location.search).get("viewId");
    if (!viewId || lastViewIdRef.current === viewId) return;

    if (!hasSession) return;

    lastViewIdRef.current = viewId;
    setStatus("loading");

    // Fire-and-forget: last-accessed is a MUTATION, so it 409s
    // SERVICE_NOT_CUT_OVER for the entire pre-cutover period and `v2/views`
    // is on the REST pass-through allowlist, so ky throws. Without this
    // catch every view open would produce an unhandled rejection.
    userService.viewsV2.touchLastAccessed(viewId).catch(() => {});

    userService.viewsV2
      .get(viewId)
      .then((detail) => {
        dispatch(viewDetailLoaded(detail));
        setStatus("loaded");
      })
      .catch((err: unknown) => {
        const httpStatus = err instanceof HTTPError ? err.response.status : undefined;
        // The ref-guard above is left set either way: a transient failure
        // still counts as "we tried this viewId" and must not retry-spam.
        setStatus(httpStatus !== undefined && GONE_STATUS_CODES.has(httpStatus) ? "gone" : "idle");
      });
  }, [location.search, dispatch, userId, hasSession]);

  return { status };
};

export default useViewLanding;
